import axios, { CancelTokenSource } from "axios";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  trackSearch,
  trackButtonClick,
  trackMoodAnalysis,
  trackQuestionSubmission,
  trackLanguageChange,
} from "./utils/gtag";
import { useTranslation } from "react-i18next";
// import { useNavigate } from "react-router-dom"; // Navigation disabled
import backgroundImage from "./bg.JPG";

import "./App.css";

const App: React.FC = () => {
  const isLoadingRef = useRef(false);
  const isLunarDateDetailLoadingRef = useRef(false);
  const { t, i18n } = useTranslation();
  // const navigate = useNavigate(); // Navigation disabled
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showMoodResult, setShowMoodResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [qimenAnalysisResult, setQimenAnalysisResult] = useState<any>(null);
  const [qimenResult, setQimenResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cards, setCards] = useState<any[]>([]);
  const [hasUserClosedModal, setHasUserClosedModal] = useState(false);
  // Animation disabled
  // const [isAnimating, setIsAnimating] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarAnalysisData, setCalendarAnalysisData] = useState(null);
  const [progress, setProgress] = useState(0);
  // Progress animation ref removed - no longer needed
  

  const formattedDate = useMemo(() => {
    const date = new Date();
    // Convert to HKT by adding 8 hours (in ms)
    const hktDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    const dateString = hktDate.toISOString().split("T")[0];
    const timeString = hktDate.toISOString().split("T")[1].split(":");
    return dateString + "-" + timeString[0] + "-" + timeString[1];
  }, []);

  const lunarRequestRef = useRef<CancelTokenSource | null>(null);
  const qimenRequestRef = useRef<CancelTokenSource | null>(null);

  const _fetchLunarDateDetail = useCallback(async () => {
    if (lunarRequestRef.current) {
      lunarRequestRef.current.cancel("New lunar request - cancel previous");
    }
    lunarRequestRef.current = axios.CancelToken.source();
    isLunarDateDetailLoadingRef.current = true;
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL!! +
          "/api/v1/qimen/lunar_date_detail?date=" +
          formattedDate,
        { cancelToken: lunarRequestRef.current.token }
      );
      const language = i18n.language.split("-")[0] === "en" ? "en" : "zh";
      setCalendarData(response.data.calendar[language]);
      setCalendarAnalysisData(response.data.calendar);
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        console.error("Lunar detail fetch error:", err);
      }
    } finally {
      isLunarDateDetailLoadingRef.current = false;
    }
  }, [formattedDate, i18n.language]);

  const _fetchQimenResult = useCallback(async () => {
    try {
      if (qimenRequestRef.current) {
        qimenRequestRef.current.cancel("New qimen request - cancel previous");
      }
      qimenRequestRef.current = axios.CancelToken.source();
      isLoadingRef.current = true;
      setIsLoading(true);

      const response = await axios.get(
        process.env.REACT_APP_API_URL!! +
          "/api/v1/qimen/high_level_prediction?date=" +
          formattedDate +
          "&question=" +
          encodeURIComponent(query) +
          "&language=" +
          i18n.language,
        { cancelToken: qimenRequestRef.current.token }
      );

      setQimenAnalysisResult(response.data.result.analysis);
      const language = i18n.language.split("-")[0] === "en" ? "en" : "zh";
      setQimenResult(response.data.result.analysis[language]);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Search error:", error);
      }
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
      setShowSearch(false);
      setShowMoodResult(true);
    }
  }, [i18n.language, query, formattedDate]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    isLoadingRef.current = true;
    setHasUserClosedModal(false);

    // Track search event
    trackSearch(query);
    trackQuestionSubmission(query);

    _fetchQimenResult();
  };

  const handleMoodClick = async () => {
    trackButtonClick("mood_analysis");
    trackMoodAnalysis();
    setIsLoading(true);
    setShowMoodResult(true);
    setHasUserClosedModal(false);
    _fetchQimenResult();
  };

  const handleQuestionClick = () => {
    trackButtonClick("ask_question");
    setShowSearch(true);
  };

  

  // Game functionality disabled
  // const handleGameClick = () => {
  //   trackButtonClick("game_view");
  //   navigate("/game");
  // };

  const handleBackClick = () => {
    trackButtonClick("back_to_home");
    setShowSearch(false);
    setShowMoodResult(false);
    setQuery("");
  };

  const changeLanguage = (lng: string) => {
    trackLanguageChange(lng);
    i18n.changeLanguage(lng);

    if (qimenAnalysisResult) {
      setQimenResult(qimenAnalysisResult[lng]);
    }

    if (calendarAnalysisData) {
      setCalendarData(calendarAnalysisData[lng]);
    }
  };

  const renderLogoLetters = useCallback((text: string) => {
    return text.split("").map((letter, index) => (
      <span key={index} className={`letter letter-${index + 1}`}>
        {letter}
      </span>
    ));
  }, []);

  const processQimenResult = useCallback((htmlContent: string, calendarData?: any) => {
    if (!htmlContent) return [];

    // Split by double newlines to get natural content sections
    const sections = htmlContent.split(/\d+\./);

    const cleanedSections: { content: string; index: number }[] = [];

    sections.forEach((section, index) => {
      // Remove HTML tags and clean up the content
      const cleanSection = section
        .replace(/<[^>]*>/g, "") // Remove HTML tags
        .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
        .replace(/&amp;/g, "&") // Replace &amp; with &
        .replace(/&lt;/g, "<") // Replace &lt; with <
        .replace(/&gt;/g, ">") // Replace &gt; with >
        .replace(/&quot;/g, '"') // Replace &quot; with "
        .replace(/•/g, "") // Replace • with ""
        .trim();

      if (cleanSection) {
        cleanedSections.push({ content: cleanSection, index });
      }
    });

    // Create cards array starting with Chinese calendar card if data is provided
    const cards = [];

    // Add Chinese calendar card as first card if data is provided
    if (calendarData) {
      cards.push({
        title: t("calendarTitle"),
        roman: "📅",
        theme: "calendar",
        type: "calendar",
        calendarData: calendarData,
      });
    }

    // Create remaining cards with different themes and distribute content
    const totalSections = cleanedSections.length;
    const sectionsPerCard = Math.max(1, Math.ceil(totalSections / 5));

    cards.push(
      {
        title: "ENERGY",
        roman: "I",
        theme: "sun",
        sections: cleanedSections.slice(0, sectionsPerCard),
      },
      {
        title: "EMOTION",
        roman: "II",
        theme: "moon",
        sections: cleanedSections.slice(sectionsPerCard, sectionsPerCard * 2),
      },
      {
        title: "MIND",
        roman: "III",
        theme: "star",
        sections: cleanedSections.slice(
          sectionsPerCard * 2,
          sectionsPerCard * 3
        ),
      },
      {
        title: "ACTION",
        roman: "IV",
        theme: "strength",
        sections: cleanedSections.slice(
          sectionsPerCard * 3,
          sectionsPerCard * 4
        ),
      },
      {
        title: "SPIRIT",
        roman: "V",
        theme: "mystic",
        sections: cleanedSections.slice(
          sectionsPerCard * 4,
          sectionsPerCard * 5
        ),
      },
      {
        title: "DESTINY",
        roman: "VI",
        theme: "world",
        sections: cleanedSections.slice(sectionsPerCard * 5),
      }
    );

    return cards.filter(
      (card) =>
        (card.sections && card.sections.length > 0) || card.type === "calendar"
    );
  }, [t]);

  // Energy stars component: up to 5 stars representing a 0–10 score
  const EnergyStars: React.FC<{ score: number }> = ({ score }) => {
    const clamped = Math.max(0, Math.min(10, score));
    const filled = Math.round(clamped / 2);
    return (
      <div className="energy-stars" role="img" aria-label="Energy rating">
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            className={`energy-star ${i < filled ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const renderCardContent = useCallback((
    sections: { content: string; index: number }[],
    cardTitle?: string,
  ) => {
    return sections
      .map(({ content, index }) => {
        // Split content into lines for further processing
        const lines = content.split(/\n+/);

        return lines
          .map((line, lineIndex) => {
            const cleanLine = line.trim();
            if (!cleanLine) return null;

            const key = `${index}-${lineIndex}`;

            // Extract optional leading title from enumerated lines, e.g. "1. 自身狀態（能量分數：6/10）：..."
            const titleMatch = cleanLine.match(/^(?:\d+\.\s*)?([^：:（(]+?)(?:（[^）)]*）|\([^)]*\))?\s*[：:]\s*(.*)$/);
            const extractedTitle = titleMatch ? titleMatch[1].trim() : null;
            const lineBody = titleMatch ? titleMatch[2].trim() : cleanLine;

            // For ENERGY card, extract and render only the score snippet (e.g., "能量分數：6/10"),
            // and also render the title (if any) and the rest of the line content below it
            if (cardTitle === "ENERGY") {
              const match = cleanLine.match(/(?:能量分數|Energy Score)[：: ]\s*(\d{1,2})\s*\/\s*10/i);
              if (match) {
                // Remove the bracketed score and optional following colon from the original line for remainder
                const remainder = cleanLine
                  .replace(/（(?:能量分數|Energy Score)[：: ]\s*\d{1,2}\s*\/\s*10）[：:]?/i, "")
                  .replace(/\((?:能量分數|Energy Score)[：: ]\s*\d{1,2}\s*\/\s*10\)[：:]?/i, "")
                  .replace(/^(?:\d+\.\s*)?([^：:（(]+?)(?:（[^）)]*）|\([^)]*\))?\s*[：:]\s*/ , "")
                  .trim();
                const elements: React.ReactNode[] = [];
                if (extractedTitle) {
                  elements.push(
                    <p key={`${key}-title`} className="card-paragraph">
                      <strong>{extractedTitle}</strong>
                    </p>
                  );
                }
                elements.push(
                  <p key={`${key}-score`} className="card-paragraph">
                    {match[0]}
                  </p>
                );
                if (remainder) {
                  elements.push(
                    <p key={`${key}-rest`} className="card-paragraph">
                      {remainder}
                    </p>
                  );
                }
                return elements;
              }
            }

            // Check if line contains special formatting (process the body after title extraction)
            if (lineBody.includes("**") || lineBody.includes("*")) {
              // Handle bold and italic text
              const parts = lineBody.split(/(\*\*.*?\*\*|\*.*?\*)/g);
              return [
                extractedTitle && (
                  <p key={`${key}-title`} className="card-paragraph">
                    <strong>{extractedTitle}</strong>
                  </p>
                ),
                (
                  <p key={`${key}-body`} className="card-paragraph">
                    {parts.map((part: string, partIndex: number) => {
                      if (part.startsWith("**") && part.endsWith("**")) {
                        return (
                          <strong key={partIndex}>{part.slice(2, -2)}</strong>
                        );
                      } else if (part.startsWith("*") && part.endsWith("*")) {
                        return <em key={partIndex}>{part.slice(1, -1)}</em>;
                      }
                      return part;
                    })}
                  </p>
                )
              ].filter(Boolean);
            }

            // Check if line looks like a header
            if (lineBody.match(/^[A-Z\s:]+:$/) || lineBody.startsWith("#")) {
              const headerText = lineBody.replace(/^#+\s*/, "");
              return [
                extractedTitle && (
                  <p key={`${key}-title`} className="card-paragraph">
                    <strong>{extractedTitle}</strong>
                  </p>
                ),
                (
                  <h4 key={`${key}-header`} className="card-header">
                    {headerText}
                  </h4>
                )
              ].filter(Boolean);
            }

            // Check if line looks like a list item
            if (lineBody.match(/^[-•*]\s/) || lineBody.match(/^\d+\.\s/)) {
              return [
                extractedTitle && (
                  <p key={`${key}-title`} className="card-paragraph">
                    <strong>{extractedTitle}</strong>
                  </p>
                ),
                (
                  <div key={`${key}-list`} className="card-list-item">
                    {lineBody}
                  </div>
                )
              ].filter(Boolean);
            }

            // Regular paragraph
            return [
              extractedTitle && (
                <p key={`${key}-title`} className="card-paragraph">
                  <strong>{extractedTitle}</strong>
                </p>
              ),
              (
                <p key={`${key}-paragraph`} className="card-paragraph">
                  {lineBody}
                </p>
              )
            ].filter(Boolean);
          })
          .filter(Boolean);
      })
      .flat();
  }, []);

  // Simple progress bar component
  const ProgressBar: React.FC<{ value: number }> = ({ value }) => {
    const clamped = Math.max(0, Math.min(100, value));
    return (
      <div className="progress-container" aria-label="Loading">
        <div
          className="progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(clamped)}
        >
          <div className="progress-fill" style={{ width: `${clamped}%` }} />
        </div>
        <div className="progress-percentage">{Math.round(clamped)}%</div>
      </div>
    );
  };

  const openModal = useCallback(() => {
    if (qimenResult) {
      const processedCards = processQimenResult(qimenResult, calendarData);
      setCards(processedCards);
      setCurrentCardIndex(0);
      setShowModal(true);
    }
  }, [qimenResult, calendarData, processQimenResult]);

  const closeModal = () => {
    setShowModal(false);
    setCurrentCardIndex(0);
    setHasUserClosedModal(true);
  };

  const nextCard = () => {
    if (currentCardIndex >= cards.length - 1) return;
    setCurrentCardIndex((prev) => prev + 1);
  };

  const prevCard = () => {
    if (currentCardIndex <= 0) return;
    setCurrentCardIndex((prev) => prev - 1);
  };

  const renderChineseCalendarCard = (calendarData: any) => {
    return (
      <div className="calendar-card-content">
        <div className="calendar-section">
          <div className="calendar-row">
            <span className="calendar-emoji">📅</span>
            <span className="calendar-label">{t("date")}</span>
            <span className="calendar-value">{calendarData.date}</span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">🌙</span>
            <span className="calendar-label">{t("lunar")}</span>
            <span className="calendar-value">{calendarData.lunar}</span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">📆</span>
            <span className="calendar-label">{t("weekday")}</span>
            <span className="calendar-value">{calendarData.weekday}</span>
          </div>
          {calendarData.festival && calendarData.festival[1] && (
            <div className="calendar-row festival">
              <span className="calendar-emoji">🎉</span>
              <span className="calendar-label">{t("festival")}</span>
              <span className="calendar-value">{calendarData.festival[1]}</span>
            </div>
          )}
        </div>

        <div className="calendar-section">
          <div className="calendar-row">
            <span className="calendar-emoji">🔢</span>
            <span className="calendar-label">{t("bazi")}</span>
            <span className="calendar-value">{calendarData.bazi}</span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">🌿</span>
            <span className="calendar-label">{t("season")}</span>
            <span className="calendar-value">{calendarData.season}</span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">🐰</span>
            <span className="calendar-label">{t("zodiacConflict")}</span>
            <span className="calendar-value">
              {calendarData.zodiacConflict}
            </span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">♊</span>
            <span className="calendar-label">{t("constellation")}</span>
            <span className="calendar-value">{calendarData.constellation}</span>
          </div>
        </div>

        <div className="calendar-section">
          <div className="calendar-row">
            <span className="calendar-emoji">⭐</span>
            <span className="calendar-label">{t("starPosition")}</span>
            <span className="calendar-value">{calendarData.starPosition}</span>
          </div>
          <div className="calendar-row">
            <span className="calendar-emoji">🚫</span>
            <span className="calendar-label">{t("pengzuTaboosSimple")}</span>
            <span
              className="calendar-value"
              dangerouslySetInnerHTML={{
                __html: calendarData.pengzuTaboosSimple,
              }}
            ></span>
          </div>
          {calendarData.twelveSpirits && (
            <div className="calendar-row">
              <span className="calendar-emoji">👻</span>
              <span className="calendar-label">{t("twelveSpirits")}</span>
              <span className="calendar-value">
                {calendarData.twelveSpirits.join(" ")}
              </span>
            </div>
          )}
        </div>

        <div className="calendar-section">
          <div className="calendar-row directions">
            <span className="calendar-emoji">🧭</span>
            <span className="calendar-label">{t("auspiciousDirections")}</span>
            <div className="calendar-directions">
              {calendarData.auspiciousDirections?.map(
                (direction: string, index: number) => (
                  <span key={index} className="direction-item">
                    {direction}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        <div className="calendar-section activities">
          <div className="calendar-activities-row good">
            <span className="calendar-emoji">✅</span>
            <span className="calendar-label">{t("suitable")}</span>
            <div className="activities-list">
              {calendarData.suitable?.map((activity: string, index: number) => (
                <span key={index} className="activity-item good">
                  {activity}
                </span>
              ))}
            </div>
          </div>
          <div className="calendar-activities-row bad">
            <span className="calendar-emoji">❌</span>
            <span className="calendar-label">{t("forbidden")}</span>
            <div className="activities-list">
              {calendarData.forbidden?.map(
                (activity: string, index: number) => (
                  <span key={index} className="activity-item bad">
                    {activity}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCard = (card: any) => {
    if (card.type === "calendar") {
      return (
        <div
          className={`modal-tarot-card tarot-card-${card.theme}`}
        >
          <div className="tarot-card-inner">
            {/* Card Header */}
            <div className="tarot-card-header">
              <div className="card-roman">{card.roman}</div>
              <div className="card-decorative-stars">
                <span className="star">✦</span>
                <span className="star">✧</span>
              </div>
            </div>

            {/* Calendar Card Content */}
            <div className="tarot-card-content calendar-content">
              {renderChineseCalendarCard(card.calendarData)}
            </div>

            {/* Card Footer */}
            <div className="tarot-card-footer">
              <div className="card-title">{card.title}</div>
              <div className="card-decorative-bottom">
                <span className="diamond">♦</span>
                <span className="plus">✚</span>
                <span className="diamond">♦</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Helper to extract energy score from sections content
    const extractEnergyScore = (sections: { content: string; index: number }[]): number | null => {
      for (const { content } of sections) {
        const lines = content.split(/\n+/);
        for (const line of lines) {
          const m = line.trim().match(/(?:能量分數|Energy Score)[：: ]\s*(\d{1,2})\s*\/\s*10/i);
          if (m) {
            const val = parseInt(m[1], 10);
            if (!isNaN(val)) return Math.max(0, Math.min(10, val));
          }
        }
      }
      return null;
    };

    const energyScore = card.title === "ENERGY" ? extractEnergyScore(card.sections) : null;

    return (
      <div
        className={`modal-tarot-card tarot-card-${card.theme}`}
      >
        <div className="tarot-card-inner">
          {/* Card Header */}
          <div className="tarot-card-header">
            <div className="card-roman">{card.roman}</div>
            <div className="card-decorative-stars">
              <span className="star">✦</span>
              <span className="star">✧</span>
            </div>
          </div>

          {/* Card Content */}
          <div className="tarot-card-content">
            <div className="card-symbol">
              {card.theme === "sun" && "☀"}
              {card.theme === "moon" && "☽"}
              {card.theme === "star" && "⭐"}
              {card.theme === "strength" && "⚡"}
              {card.theme === "mystic" && "🔮"}
              {card.theme === "world" && "🌍"}
            </div>
            {card.title === "ENERGY" && energyScore !== null && (
              <div className="energy-stars-container">
                <EnergyStars score={energyScore} />
              </div>
            )}
            <div className="card-text-content">
              {renderCardContent(card.sections, card.title)}
            </div>
          </div>

          {/* Card Footer */}
          <div className="tarot-card-footer">
            <div className="card-title">{card.title}</div>
            <div className="card-decorative-bottom">
              <span className="diamond">♦</span>
              <span className="plus">✚</span>
              <span className="diamond">♦</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!isLunarDateDetailLoadingRef.current && !calendarData) {
      _fetchLunarDateDetail();
    }
  }, [_fetchLunarDateDetail, calendarData]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showModal) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Add CSS classes to prevent scrolling
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      
      // Set scroll position to prevent jump
      document.body.style.top = `-${scrollY}px`;
      
      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-y', scrollY.toString());
      // Allow native scrolling inside modal content; rely on CSS overscroll-behavior and -webkit-overflow-scrolling
    } else {
      // Remove CSS classes
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Restore scroll position
      const scrollY = document.body.getAttribute('data-scroll-y');
      document.body.style.top = '';
      document.body.removeAttribute('data-scroll-y');
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
      }
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      document.body.style.top = '';
      document.body.removeAttribute('data-scroll-y');
    };
  }, [showModal]);

  // Progress animation disabled
  useEffect(() => {
    if (isLoading) {
      setProgress(50); // Static progress during loading
    } else {
      if (qimenResult) {
        setProgress(100);
      } else {
        setProgress(0);
      }
    }
  }, [isLoading, qimenResult]);

  // Auto-open modal when qimenResult is loaded (only if user hasn't closed it before)
  useEffect(() => {
    if (qimenResult && !showModal && !hasUserClosedModal) {
      openModal();
    }
  }, [qimenResult, showModal, hasUserClosedModal, openModal]);

  // Floating shapes disabled for performance
  const shapes: any[] = [];

  return (
    <div className={`app ${showModal ? 'modal-open' : ''}`}>
      {/* Animated Background */}
      <div
        className="background"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="floating-shapes">
          {shapes.map((s) => (
            <div key={s.key} className={s.cls} style={s.style} />
          ))}
        </div>

        {/* Gradient Orbs */}
        <div className="gradient-orbs">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Language Switcher */}
        <div className="language-switcher">
          <button
            onClick={() => changeLanguage("en")}
            className={`lang-btn ${i18n.language === "en" ? "active" : ""}`}
          >
            EN
          </button>
          <button
            onClick={() => changeLanguage("zh")}
            className={`lang-btn ${i18n.language === "zh" ? "active" : ""}`}
          >
            中文
          </button>
        </div>

        <div className="search-container">
          {/* Logo/Title */}
          <div className="logo">
            <h1 className="logo-text">{renderLogoLetters(t("logo"))}</h1>
          </div>

          {/* Action Buttons or Search Form */}
          {!showSearch && !showMoodResult && !isLoading ? (
            <div className="action-buttons">
              <button
                onClick={handleMoodClick}
                className="action-button mood-button"
              >
                <span className="button-icon">🌙</span>
                <span className="button-text">{t("moodButton")}</span>
              </button>
              <button
                onClick={handleQuestionClick}
                className="action-button question-button"
              >
                <span className="button-icon">❓</span>
                <span className="button-text">{t("questionButton")}</span>
              </button>
              {/* <button
                onClick={handleGameClick}
                className="action-button game-button"
              >
                <span className="button-icon">🔮</span>
                <span className="button-text">{t("gameButton", "心理測驗")}</span>
              </button> */}
            </div>
          ) : showMoodResult ? (
            <div className="mood-result-section">
              <button
                onClick={handleBackClick}
                className="back-button"
                aria-label={t("backButtonAria")}
              >
                <svg className="back-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 12H5M12 19L5 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("backButton")}
              </button>

              <div className="mood-result-container">
                <div className="mood-result-header">
                  <span className="mood-icon">🌙</span>
                  <h2 className="mood-title">
                    {t("moodResultTitle", "Today's Cosmic Insight")}
                  </h2>
                </div>

                {isLoading ? (
                  <div className="mood-loading">
                    <ProgressBar value={progress} />
                    <p className="loading-text">
                      {t("loadingMood", "Reading the cosmic energies...")}
                    </p>
                  </div>
                ) : qimenResult ? (
                  hasUserClosedModal ? (
                    <div className="mood-result-content">
                      <button onClick={openModal} className="view-cards-button">
                        <span className="button-icon">🔮</span>
                        <span className="button-text">
                          {t("viewCards", "View Your Reading")}
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="mood-result-content">
                      <p className="loading-text">
                        {t("openingReading", "Opening your cosmic reading...")}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="mood-error">
                    <p>
                      {t(
                        "moodError",
                        "Unable to read the cosmic energies right now. Please try again."
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="search-section">
              <button
                onClick={handleBackClick}
                className="back-button"
                aria-label={t("backButtonAria")}
              >
                <svg className="back-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 12H5M12 19L5 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("backButton")}
              </button>
              {/* Instructions */}
              <div className="search-instructions">
                <p className="instructions-text">
                  {t(
                    "searchInstructions",
                    "Ask your question about life, love, career, or any situation you're facing. Be specific for the most insightful reading."
                  )}
                </p>
              </div>

              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-container">
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("searchPlaceholder")}
                    className="search-input"
                    disabled={isLoading}
                    aria-label={t("searchInputAria")}
                    rows={4}
                  />
                  <button
                    type="submit"
                    className="search-button"
                    disabled={isLoading || !query.trim()}
                    aria-label={t("searchButtonAria")}
                  >
                    {isLoading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <svg
                        className="search-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        {/* <p className="subtitle">{t("subtitle")}</p> */}
        <div className="social-icons">
          <button
            className="social-icon instagram"
            onClick={() =>
              window.open(
                "https://www.instagram.com/super_qimen?igsh=cmoyamR2amphanZu&utm_source=qr",
                "_blank"
              )
            }
            aria-label="Open Instagram"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="social-icon-svg"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </button>
          <button
            className="social-icon threads"
            onClick={() =>
              window.open("https://www.threads.com/@super_qimen", "_blank")
            }
            aria-label="Open Threads"
          >
            <span
              className="social-icon-svg"
              style={{
                fontSize: "24px",
                fontWeight: "600",
                fontFamily: "system-ui, -apple-system, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
              }}
            >
              @
            </span>
          </button>
        </div>
      </footer>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button onClick={closeModal} className="modal-close-button">
              <svg viewBox="0 0 24 24" fill="none" className="close-icon">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Card Display */}
            <div className="modal-card-container">
              <div className="cards-stack">
                {(() => {
                  const visible: number[] = [];
                  if (currentCardIndex > 0) visible.push(currentCardIndex - 1);
                  visible.push(currentCardIndex);
                  if (currentCardIndex < cards.length - 1)
                    visible.push(currentCardIndex + 1);
                  return visible.map((index) => {
                    const card = cards[index];
                    const isActive = index === currentCardIndex;
                    const isPrev = index < currentCardIndex;
                    const isNext = index > currentCardIndex;
                    const distance = Math.abs(index - currentCardIndex);

                    let cardClass = "stacked-card";
                    if (isActive) cardClass += " active";
                    if (isPrev) cardClass += " prev";
                    if (isNext) cardClass += " next";

                    return (
                      <div
                        key={index}
                        className={cardClass}
                        style={
                          {
                            "--distance": distance,
                            "--total-cards": cards.length,
                            zIndex: cards.length - distance,
                          } as React.CSSProperties
                        }
                      >
                        {renderCard(card)}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Navigation Controls Row */}
            <div className="nav-controls-row">
              <button
                className="nav-arrow nav-arrow-left"
                onClick={prevCard}
                disabled={currentCardIndex <= 0}
                aria-label="Previous card"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="nav-arrow-icon"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="card-indicator">
                <span className="current-card">{currentCardIndex + 1}</span>
                <span className="card-divider">/</span>
                <span className="total-cards">{cards.length}</span>
              </div>

              <button
                className="nav-arrow nav-arrow-right"
                onClick={nextCard}
                disabled={currentCardIndex >= cards.length - 1}
                aria-label="Next card"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="nav-arrow-icon"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
